import React, { useState, useRef, useEffect } from "react";
import L, { Layer } from "leaflet";
import { statesData } from "./usa-states";
import { regionsData } from "./usa-regions";

import { Map } from "./MapContainer";
import { Subtitle } from "./Subtitle";
import { GeoJSON } from "./GeoJson";

import { MapConfig, MapData, MapControls, SubtitleRange } from "./types";
import { isInsideUSA, styleState, styleRegions } from "./map-utils";
import { createGeoJSONConfig } from "./geojson-create-config";
import { colorConfigs, middleOfUSA, initialZoom, maxZoom } from "./map-configs";

import "./map.scss";

interface MapProps {
  data: MapData;
  maps: MapConfig[];
  isStatic?: boolean;
  selectedMap: MapConfig;
  onChange: (option: MapConfig) => void;
  onClick?: () => void;
  idPrefix?: string;
}

export function MapControlled({
  maps,
  data,
  isStatic,
  onChange,
  onClick,
  selectedMap,
  idPrefix,
}: MapProps) {
  const mapRef = useRef<L.Map>();
  const infoRef = useRef<any>();
  const controlsRef = useRef<MapControls>();
  const geoStateJsonRef = useRef();
  const geoJsonRef = useRef();

  const [colorConfig, setColorConfig] = useState<SubtitleRange[]>();
  const [zoom, setZoom] = useState(initialZoom);
  const [focus, setFocus] = useState(middleOfUSA);
  const [hasValidLocation, setHasValidLocation] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation || isStatic) return;
    navigator.geolocation.getCurrentPosition((location) => {
      const { latitude, longitude } = location.coords;
      if (!isInsideUSA({ latitude, longitude })) return;

      const latlng = new L.LatLng(latitude, longitude);
      setFocus(latlng);
      setZoom(maxZoom);
      setHasValidLocation(true);
    });
  }, [isStatic]);

  useEffect(() => {
    setColorConfig(colorConfigs[selectedMap as MapConfig]);
  }, [selectedMap]);

  const onRegionHover = function onEachHover(feature: any, layer: Layer) {
    const { onClick, onMouseIn, onMouseOut } = createGeoJSONConfig({
      mapRef,
      infoRef,
      geoJsonRef,
    });

    layer.on({
      mouseover: onMouseIn,
      mouseout: onMouseOut,
      click: onClick,
    });
  };

  return (
    <Map
      onClick={onClick}
      selectedMap={selectedMap}
      data={data}
      maps={maps}
      initialFocus={focus}
      initialZoom={zoom}
      onChange={onChange}
      isStatic={Boolean(isStatic)}
      mapRef={mapRef}
      infoRef={infoRef}
      geoJsonRef={geoJsonRef}
      controlsRef={controlsRef}
      idPrefix={idPrefix}
      hasValidLocation={hasValidLocation}
    >
      {!isStatic && <Subtitle mapRef={mapRef} range={colorConfig!} />}
      {data && mapRef.current && (
        <>
          <GeoJSON
            data={statesData}
            geoJsonRef={geoStateJsonRef}
            mapRef={mapRef}
            stylePolygn={styleState}
          />
          <GeoJSON
            data={regionsData}
            geoJsonRef={geoJsonRef}
            mapRef={mapRef}
            stylePolygn={styleRegions({
              rangeGrades: colorConfig!,
              data,
            })}
            onHover={!isStatic ? onRegionHover : null}
          />
        </>
      )}
    </Map>
  );
}
